import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Bell,
  Send,
  Users,
  User,
  Loader2,
  RefreshCw,
  History,
  CheckCircle2,
  XCircle,
  Smartphone,
  AlertCircle,
  Search
} from 'lucide-react';
import { getClientsSimple } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PushNotifications = () => {
  const { user, token } = useAuth();
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  
  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState('all');
  const [userId, setUserId] = useState('');
  const [selectedClientLabel, setSelectedClientLabel] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const canSend = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    fetchStats();
    fetchHistory();
    fetchClients();
  }, []);

  const authHeaders = () => ({
    'Authorization': `Bearer ${token || localStorage.getItem('albmove_token')}`,
    'Content-Type': 'application/json'
  });

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/push-notifications/stats`, {
        headers: authHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await fetch(`${API_URL}/api/admin/push-notifications/history?limit=20`, {
        headers: authHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await getClientsSimple();
      setClients(response?.data?.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients.slice(0, 25);
    return clients
      .filter((c) => {
        const hay = `${c.full_name || ''} ${c.email || ''} ${c.phone || ''} ${c.id || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 25);
  }, [clients, clientSearch]);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Ju lutem plotësoni titullin dhe mesazhin');
      return;
    }

    if (target === 'user' && !userId.trim()) {
      toast.error('Ju lutem zgjidhni ose vendosni një përdorues');
      return;
    }

    setShowConfirm(true);
  };

  const confirmSend = async () => {
    setShowConfirm(false);
    setSending(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/push-notifications/send`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          target,
          user_id: target === 'user' ? userId.trim() : null,
          data: { source: 'admin_panel' }
        })
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && result.success && (result.sent_count || 0) > 0) {
        toast.success(result.message || `Njoftimi u dërgua tek ${result.sent_count} pajisje`);
        setTitle('');
        setBody('');
        setUserId('');
        setSelectedClientLabel('');
        setClientSearch('');
        fetchStats();
        fetchHistory();
      } else {
        const msg =
          result.message ||
          (typeof result.detail === 'string' ? result.detail : null) ||
          (response.ok ? 'Asnjë pajisje nuk mori njoftimin' : 'Dërgimi dështoi');
        toast.error(msg);
        fetchHistory();
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Gabim gjatë dërgimit');
    } finally {
      setSending(false);
    }
  };

  const selectClient = (client) => {
    setUserId(client.id);
    setSelectedClientLabel(
      `${client.full_name || 'Pa emër'}${client.email ? ` · ${client.email}` : ''}`
    );
    setClientSearch('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('sq-AL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="push-notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-outfit">Push Notifications</h1>
          <p className="text-zinc-400 mt-1">Dërgo njoftimet te përdoruesit e aplikacionit</p>
        </div>
        <Button
          variant="outline"
          onClick={() => { fetchStats(); fetchHistory(); }}
          className="border-zinc-700 hover:border-zinc-600"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Rifresko
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Me Push Token</p>
                  <p className="text-2xl font-bold text-white">{stats.users_with_push_tokens}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Total Përdorues</p>
                  <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Dërguar Sot</p>
                  <p className="text-2xl font-bold text-white">{stats.notifications_sent_today}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <History className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Total Dërguar</p>
                  <p className="text-2xl font-bold text-white">{stats.total_notifications_sent}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Send Notification Form */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Send className="w-5 h-5" />
              Dërgo Njoftim
            </CardTitle>
            <CardDescription>
              Krijo dhe dërgo njoftim tek përdoruesit
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!canSend && (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Vetëm Admin mund të dërgojë njoftime
                </p>
              </div>
            )}

            <div>
              <Label className="text-zinc-300">Destinacioni</Label>
              <Select
                value={target}
                onValueChange={setTarget}
                disabled={!canSend}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Të gjithë përdoruesit
                    </div>
                  </SelectItem>
                  <SelectItem value="user">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Përdorues specifik
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {target === 'user' && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Përdoruesi</Label>
                {selectedClientLabel && (
                  <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-zinc-800/80 border border-zinc-700">
                    <p className="text-sm text-white truncate">{selectedClientLabel}</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-zinc-400 hover:text-white"
                      onClick={() => {
                        setUserId('');
                        setSelectedClientLabel('');
                      }}
                      disabled={!canSend}
                    >
                      Ndrysho
                    </Button>
                  </div>
                )}
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Kërko me emër, email ose telefon..."
                    className="bg-zinc-800 border-zinc-700 pl-9"
                    disabled={!canSend}
                  />
                </div>
                {(clientSearch.trim() || !selectedClientLabel) && filteredClients.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-900">
                    {filteredClients.map((client) => (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => selectClient(client)}
                        disabled={!canSend}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-800 border-b border-zinc-800 last:border-0"
                      >
                        <span className="text-white">{client.full_name || 'Pa emër'}</span>
                        <span className="text-zinc-500 block text-xs truncate">
                          {[client.email, client.phone].filter(Boolean).join(' · ') || client.id}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <Input
                  value={userId}
                  onChange={(e) => {
                    setUserId(e.target.value);
                    setSelectedClientLabel('');
                  }}
                  placeholder="Ose vendos ID / email / telefon / ID kartë"
                  className="bg-zinc-800 border-zinc-700"
                  disabled={!canSend}
                />
                <p className="text-zinc-500 text-xs">
                  Mund të zgjidhni nga lista ose të vendosni email, telefon, ID kartë / NIPT, ose ID e brendshme.
                </p>
              </div>
            )}

            <div>
              <Label className="text-zinc-300">Titulli</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="p.sh. Ofertë Speciale!"
                className="bg-zinc-800 border-zinc-700 mt-1"
                disabled={!canSend}
                maxLength={50}
              />
              <p className="text-zinc-500 text-xs mt-1">{title.length}/50 karaktere</p>
            </div>

            <div>
              <Label className="text-zinc-300">Mesazhi</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Shkruani mesazhin e njoftimit..."
                className="bg-zinc-800 border-zinc-700 mt-1 min-h-[100px]"
                disabled={!canSend}
                maxLength={200}
              />
              <p className="text-zinc-500 text-xs mt-1">{body.length}/200 karaktere</p>
            </div>

            {/* Preview */}
            {(title || body) && (
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <p className="text-zinc-400 text-xs mb-2">PREVIEW</p>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Bell className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{title || 'Titulli'}</p>
                    <p className="text-zinc-400 text-sm">{body || 'Mesazhi...'}</p>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleSend}
              disabled={!canSend || sending || !title.trim() || !body.trim()}
              className="w-full bg-primary hover:bg-primary/90"
              data-testid="send-notification-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {sending ? 'Duke dërguar...' : 'Dërgo Njoftimin'}
            </Button>
          </CardContent>
        </Card>

        {/* History */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5" />
              Historiku i Njoftimeve
            </CardTitle>
            <CardDescription>
              Njoftimet e fundit të dërguara
            </CardDescription>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">Nuk ka njoftime të dërguara</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {history.map((notification) => (
                  <div
                    key={notification.id}
                    className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{notification.title}</p>
                        <p className="text-zinc-400 text-sm line-clamp-2">{notification.body}</p>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={notification.sent_count > 0 ? 'border-green-500/50 text-green-400' : 'border-red-500/50 text-red-400'}
                      >
                        {notification.sent_count > 0 ? (
                          <><CheckCircle2 className="w-3 h-3 mr-1" />{notification.sent_count}</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" />0</>
                        )}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500">
                      <span>{formatDate(notification.created_at)}</span>
                      <span>•</span>
                      <span>{notification.sent_by_name}</span>
                      {notification.target && (
                        <>
                          <span>•</span>
                          <Badge variant="outline" className="border-zinc-600 text-zinc-400 text-xs">
                            {notification.target === 'all' ? 'Të gjithë' : 
                             notification.target === 'broadcast' ? 'Broadcast' : 'Specifik'}
                          </Badge>
                        </>
                      )}
                    </div>
                    {notification.error && notification.sent_count === 0 && (
                      <p className="text-red-400/80 text-xs mt-2">{String(notification.error)}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Konfirmo Dërgimin</DialogTitle>
            <DialogDescription>
              {target === 'all' ? (
                <>Ky njoftim do të dërgohet tek <strong className="text-white">{stats?.users_with_push_tokens || 0}</strong> përdorues.</>
              ) : (
                <>Ky njoftim do të dërgohet tek {selectedClientLabel || userId || 'përdoruesi i specifikuar'}.</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 my-4">
            <p className="text-white font-medium">{title}</p>
            <p className="text-zinc-400 text-sm mt-1">{body}</p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowConfirm(false)}
              className="flex-1 border-zinc-700"
            >
              Anulo
            </Button>
            <Button
              onClick={confirmSend}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              <Send className="w-4 h-4 mr-2" />
              Dërgo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PushNotifications;
